// The bulk fixes of the Products step, computed from the lines the store
// holds and the problems the last check reported on them (bos-app#213,
// US-03..US-06). bos-api only detects the problems; the defaults are the
// client's. Pure: the reducer applies them, the steps read them.
import type { CatalogImportCode } from '@/api/handlers/resources/catalogImports';
import type {
    CatalogImportLineResult,
    CatalogImportProblem,
} from '@/api/models';
import { gelNumberOf, tetriFromDecimal } from '@/utils/money';
import type {
    CatalogImportState,
    ParsedLine,
    ParsedLinePatch,
    ParsedNumberField,
} from './catalogImportReducer';

/** The problems that have a default fix; every other problem has none. */
export type BulkFixCode = Extract<
    CatalogImportCode,
    | 'BARCODE_LOOKS_LIKE_IMEI'
    | 'BARCODE_SCIENTIFIC_NOTATION'
    | 'BARCODE_NOT_DIGITS'
    | 'VARIANT_FIELDS_DIFFER'
    | 'GROUP_FIELDS_DIFFER'
    | 'BARCODE_SHARED_ACROSS_SKUS'
>;

/** The fixes in the order "Import with suggested fixes" applies them. */
const BULK_FIX_CODES: readonly BulkFixCode[] = [
    'BARCODE_LOOKS_LIKE_IMEI',
    'BARCODE_SCIENTIFIC_NOTATION',
    'BARCODE_NOT_DIGITS',
    'VARIANT_FIELDS_DIFFER',
    'GROUP_FIELDS_DIFFER',
    'BARCODE_SHARED_ACROSS_SKUS',
];

export const isBulkFixCode = (code: string): code is BulkFixCode =>
    BULK_FIX_CODES.includes(code as BulkFixCode);

/**
 * The barcode problems of bos-api#207 (bos-app#232): an IMEI, a number
 * Excel scrambled, a code that is not a number. Their default is one
 * choice per variant, a generated barcode (`variantGeneratePatches`).
 */
export type BarcodeFixCode = Extract<
    BulkFixCode,
    | 'BARCODE_LOOKS_LIKE_IMEI'
    | 'BARCODE_SCIENTIFIC_NOTATION'
    | 'BARCODE_NOT_DIGITS'
>;

const BARCODE_FIX_CODES: readonly BarcodeFixCode[] = [
    'BARCODE_LOOKS_LIKE_IMEI',
    'BARCODE_SCIENTIFIC_NOTATION',
    'BARCODE_NOT_DIGITS',
];

export const isBarcodeFixCode = (code: string): code is BarcodeFixCode =>
    BARCODE_FIX_CODES.includes(code as BarcodeFixCode);

/** The figures `VARIANT_FIELDS_DIFFER` compares across a variant's rows. */
export const VARIANT_MISMATCH_FIELDS = [
    'salePrice',
    'wholesalePrice',
    'minimumQuantity',
] as const satisfies readonly ParsedNumberField[];

export type VariantMismatchField = (typeof VARIANT_MISMATCH_FIELDS)[number];

/**
 * The product fields `GROUP_FIELDS_DIFFER` compares across a SKU's rows,
 * with the cells of the line each one is: an entity's id and its text go
 * together, so the rows agree however the server resolves them.
 */
export const GROUP_FIELD_CELLS = {
    name: ['name'],
    category: ['categoryId', 'categoryText'],
    brand: ['brandId', 'brandText'],
    unit: ['unit'],
    vatType: ['vatType'],
} as const satisfies Record<string, readonly (keyof ParsedLine)[]>;

export type GroupFixField = keyof typeof GROUP_FIELD_CELLS;

/** One value of a differing figure, with the rows that carry it. */
export type BulkFixValue = {
    /** `null` is a blank cell. */
    value: number | null;
    rows: number[];
};

/**
 * One differing figure of a variant: its values, the most common number
 * first (the higher on a tie) and a blank last, and the one the default
 * writes (`null` when no line has a number).
 */
export type BulkFixChoice = {
    field: VariantMismatchField;
    values: BulkFixValue[];
    suggested: number | null;
};

/** One group a fix applies to: a variant, a SKU or a shared barcode. */
export type BulkFixGroup = {
    code: BulkFixCode;
    /** Tells the groups of one kind apart. */
    key: string;
    /**
     * The group's rows the store holds and the user has not excluded, in row
     * order. A barcode problem's group is a variant, and these are its rows
     * that carry the problem; its default writes every row of the variant.
     */
    rows: number[];
    /** What the default writes, by row: only the rows it changes. */
    patches: ReadonlyMap<number, ParsedLinePatch>;
    /** `VARIANT_FIELDS_DIFFER`: each differing figure's values; `[]` otherwise. */
    choices: BulkFixChoice[];
    /**
     * A barcode problem's variant is in the catalog already: its rows
     * restock it, so its default clears the incoming code and generates
     * nothing (the variant keeps its barcode, #232 rule 2). `false` for
     * every other group.
     */
    isRestock: boolean;
};

/** What the fixes read: the lines, the last check's answer and the exclusions. */
export type BulkFixSource = Pick<
    CatalogImportState,
    'lines' | 'preview' | 'exclusions'
>;

const blank = (value: string | null | undefined): boolean =>
    (value ?? '').trim() === '';

/** A code as catalog import matching compares it: trimmed, case-insensitive. */
const codeKey = (value: string | null | undefined): string =>
    (value ?? '').trim().toLowerCase();

const numberRows = (value: unknown): number[] =>
    Array.isArray(value)
        ? value.filter((row): row is number => Number.isInteger(row))
        : [];

const listedFields = (problem: CatalogImportProblem): readonly unknown[] => {
    if (problem.fields && problem.fields.length > 0) {
        return problem.fields;
    }
    const listed = problem.params?.fields;
    return Array.isArray(listed) ? listed : [];
};

/** The barcode a problem names in `params.barcode`, or `null` when it names none. */
const paramBarcode = (problem: CatalogImportProblem): string | null => {
    const barcode = problem.params?.barcode;
    return typeof barcode === 'string' ? barcode : null;
};

type ProblemGroup = {
    key: string;
    rows: number[];
    problem: CatalogImportProblem;
    /** A barcode problem's group: every row of its variant; `[]` otherwise. */
    variantRows: number[];
};

/** Sorted the same way whatever the locale: the order only has to be stable. */
const byCodeUnits = (a: string, b: string): number =>
    a < b ? -1 : a > b ? 1 : 0;

/**
 * The option values the check resolved a row to, as bos-api compares them
 * (`LineResolver.Combination`): each type by id with its value by id, or
 * the text a new value is created from, trimmed and lower-cased; sorted,
 * so the order of the file's columns does not matter.
 */
const resolvedCombination = (result: CatalogImportLineResult): string[] =>
    result.optionValues
        .map((option) =>
            JSON.stringify(
                option.attributeValueId !== null
                    ? [option.attributeTypeId, 'v', option.attributeValueId]
                    : [option.attributeTypeId, 'p', codeKey(option.valueText)],
            ),
        )
        .sort(byCodeUnits);

/**
 * The variant a row makes or restocks, as one key (bos-app#232: the barcode
 * choice is per variant, and the API reads it from the variant's first
 * line). By the last check, as the API groups lines: the existing variant
 * a restock adds to, the product a new variant joins with the option values
 * it resolved (an alias and the value's own name are one), or the SKU group
 * a new product is made from with those values. Before a check names it, by
 * the file: the SKU and the row's option values (each type and value by id,
 * else by text, trimmed and lower-cased), or the row alone without a SKU.
 * The parts are JSON-encoded, so a SKU holding `|` or `=` is never taken
 * for a SKU with options.
 */
export const variantKeyOf = (
    line: ParsedLine,
    result: CatalogImportLineResult | undefined,
): string => {
    if (result?.match === 'restock' && result.variantId) {
        return JSON.stringify(['restock', result.variantId]);
    }
    if (result?.match === 'newVariant' && result.productId) {
        return JSON.stringify([
            'newVariant',
            result.productId,
            resolvedCombination(result),
        ]);
    }
    if (result?.match === 'newProduct' && !blank(result.groupKey)) {
        return JSON.stringify([
            'newProduct',
            codeKey(result.groupKey),
            resolvedCombination(result),
        ]);
    }
    const sku = codeKey(line.sku);
    if (sku === '') {
        return JSON.stringify(['row', line.rowNumber]);
    }
    const options = (line.optionValues ?? [])
        .map((option) => ({
            type: codeKey(option.attributeTypeId ?? option.attributeTypeName),
            value: codeKey(option.attributeValueId ?? option.valueText),
        }))
        .filter((option) => option.value !== '')
        .map((option) => JSON.stringify([option.type, option.value]))
        .sort(byCodeUnits);
    return JSON.stringify(['file', sku, options]);
};

/** The rows of each variant (`variantKeyOf`), in the lines' order. */
const variantRowsOf = (
    source: BulkFixSource,
    results: ReadonlyMap<number, CatalogImportLineResult>,
): { keyOf: Map<number, string>; rowsOf: Map<string, number[]> } => {
    const keyOf = new Map<number, string>();
    const rowsOf = new Map<string, number[]>();
    for (const line of source.lines) {
        const key = variantKeyOf(line, results.get(line.rowNumber));
        keyOf.set(line.rowNumber, key);
        const rows = rowsOf.get(key);
        if (rows) {
            rows.push(line.rowNumber);
        } else {
            rowsOf.set(key, [line.rowNumber]);
        }
    }
    return { keyOf, rowsOf };
};

/** The row's group for a problem: its key and every row it names. */
const problemGroupOf = (
    code: Exclude<BulkFixCode, BarcodeFixCode>,
    line: CatalogImportLineResult,
    problem: CatalogImportProblem,
): { key: string; rows: number[] } => {
    const sorted = (rows: number[]): number[] =>
        [...new Set([...rows, line.rowNumber])].sort((a, b) => a - b);
    switch (code) {
        case 'GROUP_FIELDS_DIFFER': {
            // The preview's SKU group: new-product lines carry it.
            const rows = sorted(line.groupRows);
            return { key: line.groupKey ?? `rows:${rows.join(',')}`, rows };
        }
        case 'VARIANT_FIELDS_DIFFER':
        case 'BARCODE_SHARED_ACROSS_SKUS': {
            // New-variant and restock lines have no group key: `params.rows`.
            const rows = sorted(numberRows(problem.params?.rows));
            return { key: `rows:${rows.join(',')}`, rows };
        }
    }
};

/**
 * The groups the last check reported a kind on, once each: from the rows
 * that carry the problem (the user's excluded rows only with
 * `withExcluded`), in row order. A barcode problem's group is the variant
 * of each such row, with every one of its rows that carries the problem.
 */
const problemGroups = (
    source: BulkFixSource,
    code: BulkFixCode,
    withExcluded = false,
): ProblemGroup[] => {
    const groups = new Map<string, ProblemGroup>();
    const variants = isBarcodeFixCode(code)
        ? variantRowsOf(source, resultsByRowOf(source))
        : null;
    for (const line of source.preview?.lines ?? []) {
        if (!withExcluded && source.exclusions.has(line.rowNumber)) {
            continue;
        }
        const problem = line.problems.find(
            (candidate) => candidate.code === code,
        );
        if (!problem) {
            continue;
        }
        if (variants !== null) {
            const key =
                variants.keyOf.get(line.rowNumber) ?? `row:${line.rowNumber}`;
            const group = groups.get(key);
            if (group) {
                group.rows.push(line.rowNumber);
            } else {
                groups.set(key, {
                    key,
                    rows: [line.rowNumber],
                    problem,
                    variantRows: variants.rowsOf.get(key) ?? [line.rowNumber],
                });
            }
            continue;
        }
        const { key, rows } = problemGroupOf(
            code as Exclude<BulkFixCode, BarcodeFixCode>,
            line,
            problem,
        );
        if (!groups.has(key)) {
            groups.set(key, { key, rows, problem, variantRows: [] });
        }
    }
    return [...groups.values()].map((group) => ({
        ...group,
        rows: [...group.rows].sort((a, b) => a - b),
    }));
};

// ---- The defaults -----------------------------------------------------------

/**
 * What one barcode problem writes to its row for a generated barcode: an
 * IMEI or a code that is not a number moves to a blank Lot number (dropped
 * when Lot number is filled in) and Barcode is cleared; a value Excel
 * scrambled is dropped from its cell (`fields`: Barcode, or Lot number),
 * never moved. Nothing when an edit since the check changed or cleared the
 * cell the problem names.
 */
const barcodeCellPatch = (
    line: ParsedLine,
    problem: CatalogImportProblem,
): ParsedLinePatch => {
    if (problem.code === 'BARCODE_SCIENTIFIC_NOTATION') {
        const field = (problem.fields ?? []).includes('lotNumber')
            ? 'lotNumber'
            : 'barcode';
        const value = problem.params?.value;
        if (
            blank(line[field]) ||
            (typeof value === 'string' &&
                codeKey(value) !== codeKey(line[field]))
        ) {
            return {};
        }
        return { [field]: null };
    }
    const named = paramBarcode(problem);
    if (
        blank(line.barcode) ||
        (named !== null && codeKey(named) !== codeKey(line.barcode))
    ) {
        return {};
    }
    return blank(line.lotNumber)
        ? { lotNumber: (line.barcode ?? '').trim(), barcode: null }
        : { barcode: null };
};

/** A scrambled value first: dropping it from Lot number frees the cell an IMEI moves to. */
const byScrambledFirst = (
    a: CatalogImportProblem,
    b: CatalogImportProblem,
): number =>
    Number(b.code === 'BARCODE_SCIENTIFIC_NOTATION') -
    Number(a.code === 'BARCODE_SCIENTIFIC_NOTATION');

/**
 * A variant's barcode generated (`barcodeChoice: 'generate'`, bos-app#232),
 * the default of every barcode problem: on each row the cells its barcode
 * problems name are answered (`barcodeCellPatch`), and every row of a new
 * variant asks for the generated barcode, so the rows agree whichever
 * problem each has (12 IMEIs and one scrambled number make one choice). A
 * restock row asks for none: the API ignores `generate` there. Nothing
 * when no cell is left to answer (every one edited since the check, or the
 * default applied already), so a barcode the owner typed since is not
 * turned into an alternate. Only the rows it changes.
 */
const variantGeneratePatches = (
    lines: readonly ParsedLine[],
    results: ReadonlyMap<number, CatalogImportLineResult>,
): Map<number, ParsedLinePatch> => {
    const cellPatches = new Map<number, ParsedLinePatch>();
    for (const line of lines) {
        const problems = (results.get(line.rowNumber)?.problems ?? [])
            .filter((problem) => isBarcodeFixCode(problem.code))
            .sort(byScrambledFirst);
        let current = line;
        let patch: ParsedLinePatch = {};
        for (const problem of problems) {
            const cells = barcodeCellPatch(current, problem);
            current = { ...current, ...cells };
            patch = { ...patch, ...cells };
        }
        if (Object.keys(patch).length > 0) {
            cellPatches.set(line.rowNumber, patch);
        }
    }
    const patches = new Map<number, ParsedLinePatch>();
    if (cellPatches.size === 0) {
        return patches;
    }
    for (const line of lines) {
        const patch: ParsedLinePatch = { ...cellPatches.get(line.rowNumber) };
        if (
            results.get(line.rowNumber)?.match !== 'restock' &&
            line.barcodeChoice !== 'generate'
        ) {
            patch.barcodeChoice = 'generate';
        }
        if (Object.keys(patch).length > 0) {
            patches.set(line.rowNumber, patch);
        }
    }
    return patches;
};

/**
 * What the owner chose for a variant's barcode in the row drawer
 * (bos-app#232, AC-02..AC-04): a generated barcode, the file's codes kept
 * as they are, or the product's barcode typed (1 to 14 digits).
 */
export type BarcodeChoicePick =
    { kind: 'generate' } | { kind: 'keep' } | { kind: 'type'; barcode: string };

/**
 * What one barcode problem writes to its row when the codes are kept: an
 * IMEI or a code that is not a number stays in Barcode and is copied to a
 * blank Lot number; a value Excel scrambled is dropped, never kept.
 * Nothing when an edit since the check changed or cleared the cell.
 */
const barcodeKeepCellPatch = (
    line: ParsedLine,
    problem: CatalogImportProblem,
): ParsedLinePatch => {
    if (problem.code === 'BARCODE_SCIENTIFIC_NOTATION') {
        return barcodeCellPatch(line, problem);
    }
    const named = paramBarcode(problem);
    if (
        blank(line.barcode) ||
        (named !== null && codeKey(named) !== codeKey(line.barcode)) ||
        !blank(line.lotNumber)
    ) {
        return {};
    }
    return { lotNumber: (line.barcode ?? '').trim() };
};

/** The patch without the cells the line already holds as written. */
const changedCells = (
    line: ParsedLine,
    patch: ParsedLinePatch,
): ParsedLinePatch =>
    Object.fromEntries(
        Object.entries(patch).filter(
            ([field, value]) => line[field as keyof ParsedLine] !== value,
        ),
    );

/**
 * A variant's barcode as the owner chose it, on every row of the variant
 * so the rows agree (the API reads the first line's choice):
 * - `generate`: the default (`variantGeneratePatches`);
 * - `keep`: each row's barcode stays as written, copied to a blank Lot
 *   number, and every row says `barcodeChoice: 'keep'` (a restock row too:
 *   it marks the matched variant kept);
 * - `type`: each row's code, a problem's or a row's own, moves to a blank
 *   Lot number (dropped when Lot number is filled in), and every row takes
 *   the typed digits as its barcode with no `barcodeChoice` (bos-app#236).
 *
 * A value Excel scrambled is dropped whatever the choice. `generate` leaves
 * a row that gives a valid code as it is: bos-api keeps that code as an
 * alternate of the generated one (#232 contract rule 3). Only the rows it
 * changes.
 */
const variantChoicePatches = (
    lines: readonly ParsedLine[],
    results: ReadonlyMap<number, CatalogImportLineResult>,
    choice: BarcodeChoicePick,
): Map<number, ParsedLinePatch> => {
    if (choice.kind === 'generate') {
        return variantGeneratePatches(lines, results);
    }
    const cellPatchOf =
        choice.kind === 'keep' ? barcodeKeepCellPatch : barcodeCellPatch;
    const patches = new Map<number, ParsedLinePatch>();
    for (const line of lines) {
        const problems = (results.get(line.rowNumber)?.problems ?? [])
            .filter((problem) => isBarcodeFixCode(problem.code))
            .sort(byScrambledFirst);
        let current = line;
        for (const problem of problems) {
            current = { ...current, ...cellPatchOf(current, problem) };
        }
        if (choice.kind === 'keep') {
            current = { ...current, barcodeChoice: 'keep' };
        } else {
            // A row's own code that is not the typed one, a problem or
            // not, moves to a blank Lot number before it is replaced.
            const own = (current.barcode ?? '').trim();
            const moves =
                own !== '' &&
                codeKey(own) !== codeKey(choice.barcode) &&
                blank(current.lotNumber);
            current = {
                ...current,
                ...(moves ? { lotNumber: own } : {}),
                barcode: choice.barcode,
                barcodeChoice: undefined,
            };
        }
        const patch = changedCells(line, current);
        if (Object.keys(patch).length > 0) {
            patches.set(line.rowNumber, patch);
        }
    }
    return patches;
};

/**
 * The variant of `rowNumber` for its barcode choice (`variantKeyOf`): the
 * rows of it the store holds and the user has not excluded, in the lines'
 * order, and the barcode problems the last check reported on them. `null`
 * when the row is excluded or no such row has a barcode problem.
 */
export type BarcodeVariant = {
    key: string;
    lines: ParsedLine[];
    /** The barcode problems by row, a row without any left out. */
    problems: ReadonlyMap<number, CatalogImportProblem[]>;
    /** The variant is in the catalog already: the rows restock it. */
    isRestock: boolean;
};

export const barcodeVariantOf = (
    source: BulkFixSource,
    rowNumber: number,
): BarcodeVariant | null => {
    if (source.exclusions.has(rowNumber)) {
        return null;
    }
    const results = resultsByRowOf(source);
    const { keyOf, rowsOf } = variantRowsOf(source, results);
    const key = keyOf.get(rowNumber);
    if (key === undefined) {
        return null;
    }
    const linesByRow = linesByRowOf(source.lines);
    const lines = (rowsOf.get(key) ?? [])
        .filter((row) => !source.exclusions.has(row))
        .map((row) => linesByRow.get(row))
        .filter((line): line is ParsedLine => line !== undefined);
    const problems = new Map<number, CatalogImportProblem[]>();
    for (const line of lines) {
        const own = (results.get(line.rowNumber)?.problems ?? []).filter(
            (problem) => isBarcodeFixCode(problem.code),
        );
        if (own.length > 0) {
            problems.set(line.rowNumber, own);
        }
    }
    if (problems.size === 0) {
        return null;
    }
    return {
        key,
        lines,
        problems,
        isRestock: results.get(rowNumber)?.match === 'restock',
    };
};

/** The figures that are amounts: compared in tetri (`domain.md`, Money). */
const AMOUNT_FIELDS: readonly VariantMismatchField[] = [
    'salePrice',
    'wholesalePrice',
];

/**
 * What a figure's cell is compared and ranked by: an amount in integer
 * tetri, a count as it is, a blank `null`.
 */
const figureKey = (
    field: VariantMismatchField,
    value: number | null | undefined,
): number | null => {
    if (value === null || value === undefined) {
        return null;
    }
    return AMOUNT_FIELDS.includes(field) ? tetriFromDecimal(value) : value;
};

/** The number a key writes to the line: an amount back in decimal GEL. */
const figureOfKey = (
    field: VariantMismatchField,
    key: number | null,
): number | null =>
    key === null || !AMOUNT_FIELDS.includes(field) ? key : gelNumberOf(key);

/**
 * The values of a figure with their rows: the most common number first,
 * the higher on a tie, and a blank cell last. Amounts are counted and
 * ranked in tetri; each value is that amount in GEL, as the patch writes it.
 */
const rankedValues = (
    lines: readonly ParsedLine[],
    field: VariantMismatchField,
): BulkFixValue[] => {
    const byKey = new Map<number | null, number[]>();
    for (const line of lines) {
        const key = figureKey(field, line[field]);
        byKey.set(key, [...(byKey.get(key) ?? []), line.rowNumber]);
    }
    return [...byKey.entries()]
        .sort(([aKey, aRows], [bKey, bRows]) => {
            if (aKey === null || bKey === null) {
                return aKey === null ? 1 : -1;
            }
            return bRows.length - aRows.length || bKey - aKey;
        })
        .map(([key, rows]) => ({ value: figureOfKey(field, key), rows }));
};

/**
 * One value per differing figure: the most common number, the higher on a
 * tie. A blank cell takes no part in the count and is given that value; a
 * line whose cell is unreadable takes no part and is not changed. A figure
 * no line has a number for is left as it is.
 */
const variantFix = (
    lines: readonly ParsedLine[],
    problem: CatalogImportProblem,
): { patches: Map<number, ParsedLinePatch>; choices: BulkFixChoice[] } => {
    const listed = listedFields(problem);
    const fields = VARIANT_MISMATCH_FIELDS.filter((field) =>
        listed.includes(field),
    );
    const patches = new Map<number, ParsedLinePatch>();
    const choices: BulkFixChoice[] = [];
    for (const field of fields) {
        const readable = lines.filter(
            (line) => line.unreadable?.[field] === undefined,
        );
        const values = rankedValues(readable, field);
        const suggested = values[0]?.value ?? null;
        if (values.length === 0) {
            continue;
        }
        choices.push({ field, values, suggested });
        if (suggested === null) {
            continue;
        }
        // A line holding the suggested amount in another decimal (0.1 + 0.2
        // for 0.3) is written too: bos-api compares the numbers sent, so it
        // would still see two values.
        for (const line of readable) {
            if (line[field] !== suggested) {
                patches.set(line.rowNumber, {
                    ...patches.get(line.rowNumber),
                    [field]: suggested,
                });
            }
        }
    }
    return { patches, choices };
};

/**
 * The problems that say the check did not recognise a row's cell for a
 * product field: bos-api leaves such a row out when it compares the SKU's
 * rows, and out of `GROUP_FIELDS_DIFFER`'s fallback.
 */
const UNRECOGNISED_CODES = {
    name: ['NAME_REQUIRED'],
    category: ['NAME_TARGET_UNKNOWN', 'CATEGORY_AMBIGUOUS', 'NAME_MERGE_CYCLE'],
    brand: ['NAME_TARGET_UNKNOWN', 'BRAND_AMBIGUOUS', 'NAME_MERGE_CYCLE'],
    unit: ['UNIT_UNKNOWN'],
    vatType: ['VAT_TYPE_UNKNOWN'],
} as const satisfies Record<GroupFixField, readonly CatalogImportCode[]>;

const groupFieldsOf = (problem: CatalogImportProblem): GroupFixField[] => {
    const listed = listedFields(problem);
    return (Object.keys(GROUP_FIELD_CELLS) as GroupFixField[]).filter((field) =>
        listed.includes(field),
    );
};

/** Whether the row's cells for the field are filled in and the last check recognised them. */
const givesField = (
    line: ParsedLine,
    field: GroupFixField,
    result: CatalogImportLineResult | undefined,
): boolean => {
    const codes: readonly string[] = UNRECOGNISED_CODES[field];
    return (
        GROUP_FIELD_CELLS[field].some((cell) => !blank(line[cell])) &&
        !(result?.problems ?? []).some(
            (problem) =>
                codes.includes(problem.code) &&
                (problem.fields ?? []).includes(field),
        )
    );
};

/**
 * The default's row for each differing product field of the SKU: the first
 * by row number whose cells for it are filled in and recognised, as bos-api
 * takes it. A field no row gives is left out.
 */
const groupSources = (
    lines: readonly ParsedLine[],
    problem: CatalogImportProblem,
    results: ReadonlyMap<number, CatalogImportLineResult>,
): Map<GroupFixField, ParsedLine> => {
    const sources = new Map<GroupFixField, ParsedLine>();
    for (const field of groupFieldsOf(problem)) {
        const source = lines.find((line) =>
            givesField(line, field, results.get(line.rowNumber)),
        );
        if (source) {
            sources.set(field, source);
        }
    }
    return sources;
};

/**
 * Each differing product field of the SKU from its row (`sources`: the
 * default's, or the drawer's pick for every field) to every other row.
 */
const groupPatches = (
    lines: readonly ParsedLine[],
    sources: ReadonlyMap<GroupFixField, ParsedLine>,
): Map<number, ParsedLinePatch> => {
    const patches = new Map<number, ParsedLinePatch>();
    for (const line of lines) {
        const patch: Record<string, string | null> = {};
        for (const [field, first] of sources) {
            if (line.rowNumber === first.rowNumber) {
                continue;
            }
            const cells = GROUP_FIELD_CELLS[field];
            if (
                cells.some(
                    (cell) => (line[cell] ?? null) !== (first[cell] ?? null),
                )
            ) {
                for (const cell of cells) {
                    patch[cell] = first[cell] ?? null;
                }
            }
        }
        if (Object.keys(patch).length > 0) {
            patches.set(line.rowNumber, patch as ParsedLinePatch);
        }
    }
    return patches;
};

/**
 * The first SKU by row keeps the shared barcode; the other SKUs' lines
 * clear it and fall back to their SKU.
 */
const sharedBarcodePatches = (
    lines: readonly ParsedLine[],
    problem: CatalogImportProblem,
): Map<number, ParsedLinePatch> => {
    const patches = new Map<number, ParsedLinePatch>();
    const [first] = lines;
    if (!first) {
        return patches;
    }
    const keeper = codeKey(first.sku);
    const named = paramBarcode(problem);
    for (const line of lines) {
        if (codeKey(line.sku) === keeper || blank(line.barcode)) {
            continue;
        }
        if (named !== null && codeKey(named) !== codeKey(line.barcode)) {
            continue;
        }
        patches.set(line.rowNumber, { barcode: null });
    }
    return patches;
};

/**
 * The group's rows the store holds and the user has not excluded (every
 * one with no `exclusions`), in row order.
 */
const groupLinesOf = (
    group: ProblemGroup,
    linesByRow: ReadonlyMap<number, ParsedLine>,
    exclusions: ReadonlySet<number> = new Set(),
): ParsedLine[] =>
    group.rows
        .filter((row) => !exclusions.has(row))
        .map((row) => linesByRow.get(row))
        .filter((line): line is ParsedLine => line !== undefined);

const resultsByRowOf = (
    source: BulkFixSource,
): Map<number, CatalogImportLineResult> =>
    new Map(
        (source.preview?.lines ?? []).map((result) => [
            result.rowNumber,
            result,
        ]),
    );

const groupOf = (
    code: BulkFixCode,
    group: ProblemGroup,
    linesByRow: ReadonlyMap<number, ParsedLine>,
    source: BulkFixSource,
    results: ReadonlyMap<number, CatalogImportLineResult>,
): BulkFixGroup => {
    const lines = groupLinesOf(group, linesByRow, source.exclusions);
    const rows = lines.map((line) => line.rowNumber);
    switch (code) {
        case 'BARCODE_LOOKS_LIKE_IMEI':
        case 'BARCODE_SCIENTIFIC_NOTATION':
        case 'BARCODE_NOT_DIGITS':
            return {
                code,
                key: group.key,
                rows,
                patches: variantGeneratePatches(
                    groupLinesOf(
                        { ...group, rows: group.variantRows },
                        linesByRow,
                        source.exclusions,
                    ),
                    results,
                ),
                choices: [],
                isRestock:
                    results.get(group.variantRows[0] ?? -1)?.match ===
                    'restock',
            };
        case 'VARIANT_FIELDS_DIFFER': {
            const { patches, choices } = variantFix(lines, group.problem);
            return {
                code,
                key: group.key,
                rows,
                patches,
                choices,
                isRestock: false,
            };
        }
        case 'GROUP_FIELDS_DIFFER':
            return {
                code,
                key: group.key,
                rows,
                patches: groupPatches(
                    lines,
                    groupSources(lines, group.problem, results),
                ),
                choices: [],
                isRestock: false,
            };
        case 'BARCODE_SHARED_ACROSS_SKUS':
            return {
                code,
                key: group.key,
                rows,
                patches: sharedBarcodePatches(lines, group.problem),
                choices: [],
                isRestock: false,
            };
    }
};

const linesByRowOf = (lines: readonly ParsedLine[]): Map<number, ParsedLine> =>
    new Map(lines.map((line) => [line.rowNumber, line]));

/**
 * One kind's groups over the current lines' problems, each with its
 * default: a generated barcode per variant for the barcode problems
 * (`BarcodeFixCode`: `variantGeneratePatches`, keyed by `variantKeyOf`),
 * the most common figure per variant (`VARIANT_FIELDS_DIFFER`, keyed by
 * `params.rows`), per SKU each product field from the first row that
 * fills it in recognisably (`GROUP_FIELDS_DIFFER`, the preview's SKU
 * group) and the first SKU by row keeping a shared barcode
 * (`BARCODE_SHARED_ACROSS_SKUS`, keyed by `params.rows`). Rows the user
 * excluded take no part.
 */
export const bulkFixGroups = (
    source: BulkFixSource,
    code: BulkFixCode,
): BulkFixGroup[] => {
    const linesByRow = linesByRowOf(source.lines);
    const results = resultsByRowOf(source);
    return problemGroups(source, code).map((group) =>
        groupOf(code, group, linesByRow, source, results),
    );
};

/** The group of a kind a row belongs to (the drawer's), or `null`. */
export const bulkFixGroupOf = (
    source: BulkFixSource,
    code: BulkFixCode,
    rowNumber: number,
): BulkFixGroup | null =>
    bulkFixGroups(source, code).find((group) =>
        group.rows.includes(rowNumber),
    ) ?? null;

// ---- The drawer's pick ------------------------------------------------------

/**
 * The barcode choice for the row's variant (bos-app#232), named by one of
 * its barcode problems; generate when `choice` is left out, the default.
 */
export type BarcodeGroupPick = {
    code: BarcodeFixCode;
    choice?: BarcodeChoicePick;
};

const isBarcodeChoicePick = (pick: GroupPick): pick is BarcodeGroupPick =>
    isBarcodeFixCode(pick.code);

/**
 * What the row drawer picked for its row's group (bos-app#219, US-07): one
 * of a variant figure's values (`VARIANT_FIELDS_DIFFER`), the row of the
 * SKU whose product fields every row takes (`GROUP_FIELDS_DIFFER`), or the
 * kind's default for that group alone: the shared barcode kept on the
 * first SKU by row (`BARCODE_SHARED_ACROSS_SKUS`); or the barcode choice
 * for the row's variant (`BarcodeGroupPick`).
 */
export type GroupPick =
    | BarcodeGroupPick
    | { code: Extract<BulkFixCode, 'BARCODE_SHARED_ACROSS_SKUS'> }
    | {
          code: Extract<BulkFixCode, 'VARIANT_FIELDS_DIFFER'>;
          field: VariantMismatchField;
          /** One of the figure's values (`BulkFixChoice.values`); `null` is blank. */
          value: number | null;
      }
    | {
          code: Extract<BulkFixCode, 'GROUP_FIELDS_DIFFER'>;
          /** A row of the group: its cells for the differing fields. */
          sourceRow: number;
      };

/**
 * What the drawer's pick writes to the group of `rowNumber`: the picked
 * value of the figure to every row of the variant that carries another
 * (a line whose cell is unreadable takes no part, as for the default), or
 * the picked row's cells for the differing product fields to every other
 * row of the SKU, the default of a shared-barcode group (its `patches`),
 * or the barcode choice to every row of the row's variant
 * (`variantChoicePatches`). Only the rows it changes; nothing for a value
 * the figure does not have, a row outside the group, or a row in no group
 * of the kind (for a barcode choice: a variant none of whose rows carries
 * the kind).
 * Rows the user excluded take no part, except in the SKU's pick ("Use for
 * all rows"): it writes every row of the SKU, excluded ones included, so
 * the rows still agree when one is included again, and an excluded row
 * can be its source, as the drawer lists every row of the SKU.
 */
export const groupPickPatches = (
    source: BulkFixSource,
    rowNumber: number,
    pick: GroupPick,
): Map<number, ParsedLinePatch> => {
    const patches = new Map<number, ParsedLinePatch>();
    // A shared-barcode group: its default.
    if (pick.code === 'BARCODE_SHARED_ACROSS_SKUS') {
        return new Map(
            bulkFixGroupOf(source, pick.code, rowNumber)?.patches ?? patches,
        );
    }
    // A barcode choice: every row of the row's variant, when one of them
    // carries the kind.
    if (isBarcodeChoicePick(pick)) {
        const variant = barcodeVariantOf(source, rowNumber);
        const { code } = pick;
        if (
            !variant ||
            ![...variant.problems.values()].some((problems) =>
                problems.some((problem) => problem.code === code),
            )
        ) {
            return patches;
        }
        return variantChoicePatches(
            variant.lines,
            resultsByRowOf(source),
            pick.choice ?? { kind: 'generate' },
        );
    }
    if (pick.code === 'VARIANT_FIELDS_DIFFER') {
        const choice = bulkFixGroupOf(
            source,
            pick.code,
            rowNumber,
        )?.choices.find((candidate) => candidate.field === pick.field);
        const pickedKey = figureKey(pick.field, pick.value);
        const isPicked = (entry: BulkFixValue): boolean =>
            figureKey(pick.field, entry.value) === pickedKey;
        if (!choice?.values.some(isPicked)) {
            return patches;
        }
        const linesByRow = linesByRowOf(source.lines);
        for (const entry of choice.values) {
            for (const row of entry.rows) {
                // The picked amount's own rows are written only where their
                // decimal reads otherwise (0.1 + 0.2 for 0.3), as bos-api
                // compares the numbers sent.
                if (
                    isPicked(entry) &&
                    linesByRow.get(row)?.[pick.field] === pick.value
                ) {
                    continue;
                }
                patches.set(row, { [pick.field]: pick.value });
            }
        }
        return patches;
    }
    const group = problemGroups(source, pick.code, true).find((candidate) =>
        candidate.rows.includes(rowNumber),
    );
    if (!group) {
        return patches;
    }
    const lines = groupLinesOf(group, linesByRowOf(source.lines));
    const keeper = lines.find((line) => line.rowNumber === pick.sourceRow);
    return keeper
        ? groupPatches(
              lines,
              new Map(
                  groupFieldsOf(group.problem).map((field) => [field, keeper]),
              ),
          )
        : patches;
};

/**
 * The row the drawer marks Suggested for the SKU of `rowNumber`
 * (`GROUP_FIELDS_DIFFER`): the one the default takes every differing field
 * from, so that taking it for all rows is the default. `null` when the
 * fields come from different rows, a field has no row to come from, or the
 * row is in no such group.
 */
export const groupFieldsSuggestedRow = (
    source: BulkFixSource,
    rowNumber: number,
): number | null => {
    const group = problemGroups(source, 'GROUP_FIELDS_DIFFER').find(
        (candidate) => candidate.rows.includes(rowNumber),
    );
    if (!group) {
        return null;
    }
    const lines = groupLinesOf(
        group,
        linesByRowOf(source.lines),
        source.exclusions,
    );
    const sources = groupSources(lines, group.problem, resultsByRowOf(source));
    const rows = new Set([...sources.values()].map((line) => line.rowNumber));
    return sources.size === groupFieldsOf(group.problem).length &&
        rows.size === 1
        ? [...rows][0]!
        : null;
};

/**
 * The groups that have a fix: those whose default changes a row. A group
 * it would change none of has none, such as a variant whose figures differ
 * only by an unreadable cell (the default skips it, yet bos-api still sees
 * two values): its rows stay flagged for the owner to correct in the drawer.
 */
export const groupsWithFix = (
    groups: readonly BulkFixGroup[],
): BulkFixGroup[] => groups.filter((group) => group.patches.size > 0);

/** How many rows the groups' defaults change. */
export const changedRowCount = (groups: readonly BulkFixGroup[]): number =>
    new Set(groups.flatMap((group) => [...group.patches.keys()])).size;

/** The groups' patches as one, by row. */
export const mergedPatches = (
    groups: readonly BulkFixGroup[],
): Map<number, ParsedLinePatch> => {
    const merged = new Map<number, ParsedLinePatch>();
    for (const group of groups) {
        for (const [row, patch] of group.patches) {
            merged.set(row, { ...merged.get(row), ...patch });
        }
    }
    return merged;
};

/** The lines with the patches applied; a line no patch names is kept as it is. */
export const withPatches = (
    lines: ParsedLine[],
    patches: ReadonlyMap<number, ParsedLinePatch>,
): ParsedLine[] =>
    patches.size === 0
        ? lines
        : lines.map((line) => {
              const patch = patches.get(line.rowNumber);
              return patch ? { ...line, ...patch } : line;
          });

/**
 * What "Import with suggested fixes" changes: the lines, the rows per fix
 * (`counts`) and the groups per fix it changes a row of (`groups`:
 * variants given a generated barcode, variants, SKUs, shared barcodes; its
 * summary's counts, bos-app#221), with the restocked variants a barcode
 * fix changes counted apart (`restocks`: they keep their barcode, none is
 * generated, #232 rule 2; 0 for every other fix).
 */
export type AllFixesPlan = {
    lines: ParsedLine[];
    changedRows: number[];
    counts: Record<BulkFixCode, number>;
    groups: Record<BulkFixCode, number>;
    restocks: Record<BulkFixCode, number>;
};

/**
 * Every default, kind after kind in `BULK_FIX_CODES` order, each on the
 * lines the one before left: the plan "Import with suggested fixes"
 * applies as one bulk action.
 */
export const allFixesPlan = (source: BulkFixSource): AllFixesPlan => {
    let lines = source.lines;
    const changed = new Set<number>();
    const counts = {} as Record<BulkFixCode, number>;
    const changedGroups = {} as Record<BulkFixCode, number>;
    const restocks = {} as Record<BulkFixCode, number>;
    for (const code of BULK_FIX_CODES) {
        const groups = bulkFixGroups({ ...source, lines }, code);
        const patches = mergedPatches(groups);
        const fixed = groupsWithFix(groups);
        counts[code] = patches.size;
        changedGroups[code] = fixed.filter((group) => !group.isRestock).length;
        restocks[code] = fixed.filter((group) => group.isRestock).length;
        patches.forEach((_, row) => changed.add(row));
        lines = withPatches(lines, patches);
    }
    return {
        lines,
        changedRows: [...changed].sort((a, b) => a - b),
        counts,
        groups: changedGroups,
        restocks,
    };
};

/**
 * Two plans applied one after the other as one step: the second's lines,
 * the rows either changed, and each kind's rows and groups added up (the
 * second answers problems the first revealed, so its groups are others).
 */
export const joinedPlans = (
    first: AllFixesPlan,
    next: AllFixesPlan,
): AllFixesPlan => {
    const counts = {} as Record<BulkFixCode, number>;
    const groups = {} as Record<BulkFixCode, number>;
    const restocks = {} as Record<BulkFixCode, number>;
    for (const code of BULK_FIX_CODES) {
        counts[code] = first.counts[code] + next.counts[code];
        groups[code] = first.groups[code] + next.groups[code];
        restocks[code] = first.restocks[code] + next.restocks[code];
    }
    return {
        lines: next.lines,
        changedRows: [
            ...new Set([...first.changedRows, ...next.changedRows]),
        ].sort((a, b) => a - b),
        counts,
        groups,
        restocks,
    };
};

// ---- Make main --------------------------------------------------------------

const BARCODES_KEPT_AS_ALTERNATES =
    'BARCODES_KEPT_AS_ALTERNATES' satisfies CatalogImportCode;

/**
 * Make main: `primaryBarcode` set on the chosen line and cleared on the
 * variant's other lines, the rows the line's `BARCODES_KEPT_AS_ALTERNATES`
 * names (`params.rows`; the line alone without that warning). Only the rows
 * it changes.
 */
export const makeMainPatches = (
    source: Pick<CatalogImportState, 'lines' | 'preview'>,
    rowNumber: number,
): Map<number, ParsedLinePatch> => {
    const warning = source.preview?.lines
        .find((line) => line.rowNumber === rowNumber)
        ?.warnings.find(
            (candidate) => candidate.code === BARCODES_KEPT_AS_ALTERNATES,
        );
    const rows = new Set([...numberRows(warning?.params?.rows), rowNumber]);
    const patches = new Map<number, ParsedLinePatch>();
    for (const line of source.lines) {
        if (!rows.has(line.rowNumber)) {
            continue;
        }
        if (line.rowNumber === rowNumber) {
            if (line.primaryBarcode !== true) {
                patches.set(line.rowNumber, { primaryBarcode: true });
            }
        } else if (line.primaryBarcode !== undefined) {
            patches.set(line.rowNumber, { primaryBarcode: undefined });
        }
    }
    return patches;
};
